/**
 * Validation utilities for form field validation
 */

export interface ValidationError {
  field: string;
  message: string;
}

export type InformationRequestFieldType =
  | "email"
  | "int"
  | "boolean"
  | "datetime"
  | "phone"
  | "name"
  | "postal"
  | "string";

const ONBOARDING_SANITY_FIELD_TYPES: Record<string, InformationRequestFieldType> = {
  legal_name: "name",
  preferred_name: "name",
  given_name: "name",
  surname: "name",
  work_email: "email",
  personal_email: "email",
  requester_email: "email",
  start_date: "datetime",
};

/**
 * Infer HITL field validation without letting explanatory discrepancy text
 * change the type of a structured onboarding field.
 */
export const inferInformationRequestFieldType = (
  requestId: string,
  question: string
): InformationRequestFieldType => {
  if (requestId.startsWith("structured_workflow_resolution:")) {
    return "string";
  }

  const onboardingPrefix = "onboarding_sanity:";
  if (requestId.startsWith(onboardingPrefix)) {
    const fieldName = requestId.slice(onboardingPrefix.length);
    return ONBOARDING_SANITY_FIELD_TYPES[fieldName] || "string";
  }

  const questionLower = question.toLowerCase();
  if (questionLower.includes("email")) return "email";
  if (questionLower.includes("phone")) return "phone";
  if (questionLower.includes("postal") || questionLower.includes("zip")) return "postal";
  if (questionLower.includes("date")) return "datetime";
  if (questionLower.includes("name")) return "name";
  return "string";
};

/**
 * Validate email format
 * @param value - The email value to validate
 * @returns Error message if invalid, null if valid
 */
export const validateEmail = (value: string): string | null => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value.trim()) return null; // Empty is OK if field is optional
  if (!emailRegex.test(value)) {
    return "Please enter a valid email address (e.g., user@domain.com)";
  }
  return null;
};

/**
 * Validate integer format
 * @param value - The integer value to validate
 * @returns Error message if invalid, null if valid
 */
export const validateInteger = (value: string): string | null => {
  if (!value.trim()) return null; // Empty is OK if field is optional
  const intRegex = /^-?\d+$/;
  if (!intRegex.test(value)) {
    return "Please enter a valid integer (e.g., 123)";
  }
  return null;
};

/**
 * Validate boolean format
 * @param value - The boolean value to validate
 * @returns Error message if invalid, null if valid
 */
export const validateBoolean = (value: string): string | null => {
  if (!value.trim()) return null; // Empty is OK if field is optional
  const lowerValue = value.toLowerCase().trim();
  const validValues = ["true", "false", "yes", "no", "1", "0"];
  if (!validValues.includes(lowerValue)) {
    return "Please enter true/false, yes/no, or 1/0";
  }
  return null;
};

/**
 * Validate datetime format
 * @param value - The datetime value to validate
 * @returns Error message if invalid, null if valid
 */
export const validateDatetime = (value: string): string | null => {
  if (!value.trim()) return null; // Empty is OK if field is optional
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return "Please enter a valid date (e.g., 2024-01-15 or Jan 15, 2024)";
  }
  return null;
};

/**
 * Validate name format (no digits allowed)
 * @param value - The name value to validate
 * @returns Error message if invalid, null if valid
 */
export const validateName = (value: string): string | null => {
  if (!value.trim()) return null; // Empty is OK if field is optional
  // Check if name contains any digits
  if (/\d/.test(value)) {
    return "Name cannot contain numbers";
  }
  return null;
};

/**
 * Validate phone number format
 * @param value - The phone number value to validate
 * @returns Error message if invalid, null if valid
 */
export const validatePhone = (value: string): string | null => {
  if (!value.trim()) return null; // Empty is OK if field is optional
  // Remove spaces and special characters for validation, allow only digits, +, -, (, ), and .
  const cleanValue = value.trim();
  // Check if it has at least 10 digits (minimum for a valid phone number)
  const digitsOnly = cleanValue.replace(/\D/g, '');
  if (digitsOnly.length < 10) {
    return "Please enter a valid phone number (e.g., +1-234-567-8900 or (234) 567-8900)";
  }
  // Allow various phone formats with at least 10 digits
  const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,5}[-\s.]?[0-9]{1,5}$/;
  if (!phoneRegex.test(cleanValue)) {
    return "Please enter a valid phone number (e.g., +1-234-567-8900 or (234) 567-8900)";
  }
  return null;
};

/**
 * Validate postal code format
 * @param value - The postal code value to validate
 * @returns Error message if invalid, null if valid
 */
export const validatePostalCode = (value: string): string | null => {
  if (!value.trim()) return null; // Empty is OK if field is optional
  const cleanValue = value.trim();
  
  // Must contain at least one digit (rejects purely alphabetic like "fdfdsd")
  if (!/\d/.test(cleanValue)) {
    return "Please enter a valid postal code (e.g., 12345 or A1A 1A1)";
  }
  
  // Allow common postal code formats:
  // US: 12345 or 12345-6789
  // Canada: A1A 1A1
  // UK: SW1A 1AA
  // International: flexible pattern with letters, digits, spaces, hyphens
  const postalRegex = /^[A-Za-z0-9]{3,10}([\s-]?[A-Za-z0-9]{3,10})?$/;
  if (!postalRegex.test(cleanValue)) {
    return "Please enter a valid postal code (e.g., 12345 or A1A 1A1)";
  }
  
  return null;
};

/**
 * Validate string format
 * @param value - The string value to validate
 * @param minLength - Minimum length (default: 0)
 * @param maxLength - Maximum length (default: 1000)
 * @returns Error message if invalid, null if valid
 */
export const validateString = (
  value: string,
  minLength = 0,
  maxLength = 1000
): string | null => {
  if (!value.trim() && minLength > 0) {
    return `Minimum length is ${minLength} characters`;
  }
  if (value.length > maxLength) {
    return `Maximum length is ${maxLength} characters`;
  }
  return null;
};

/**
 * Validate field based on its type
 * @param value - The value to validate
 * @param fieldType - The type of field ("email", "int", "boolean", "datetime", "phone", "name", "postal", "string")
 * @param isRequired - Whether the field is required
 * @returns Error message if invalid, null if valid
 */
export const validateField = (value: string, fieldType: string, isRequired: boolean = false): string | null => {
  // Check if required field is empty
  if (isRequired && !value.trim()) {
    return "This field is required";
  }
  
  // Type-specific validation
  switch (fieldType) {
    case "email":
      return validateEmail(value);
    case "int":
      return validateInteger(value);
    case "boolean":
      return validateBoolean(value);
    case "datetime":
      return validateDatetime(value);
    case "phone":
      return validatePhone(value);
    case "name":
      return validateName(value);
    case "postal":
      return validatePostalCode(value);
    case "string":
      return validateString(value);
    default:
      return null; // Unknown type, no validation
  }
};

